import { blackState, ortexColorState, themeState, whiteState } from "@/lib/store";
import { classNames, nFormatter } from "@/lib/utils";
import { useMemo } from "react";
import { useAtomValue } from "jotai";
import type { XYChartOptions, XYSeries } from "@ortex-charts/react";
import { XYPlot } from "@/components/ortex-charts/Viz";
import styles from "./StockCommodityScatterPlotChart.module.scss";

export interface ScatterSeries {
    id: string;
    name: string;
    /** Daily moves as [commodity move, stock move] pairs. */
    data: [number, number][];
}

interface Props {
    series: ScatterSeries[];
    obfuscated?: boolean;
}

/** Ordinary least squares through the points, as two end points for a line. */
export function regressionLine(points: readonly (readonly [number, number])[]): [number, number][] {
    const n = points.length;
    if (n < 2) return [];
    let sx = 0;
    let sy = 0;
    let sxx = 0;
    let sxy = 0;
    for (const [x, y] of points) {
        sx += x;
        sy += y;
        sxx += x * x;
        sxy += x * y;
    }
    const den = n * sxx - sx * sx;
    if (den === 0) return [];
    const slope = (n * sxy - sx * sy) / den;
    const intercept = (sy - slope * sx) / n;
    let lo = Infinity;
    let hi = -Infinity;
    for (const [x] of points) {
        if (x < lo) lo = x;
        if (x > hi) hi = x;
    }
    return [
        [lo, intercept + slope * lo],
        [hi, intercept + slope * hi],
    ];
}

/** Stock moves against each commodity's moves, with a regression line through each cloud. */
const StockCommodityScatterPlotChart = ({ series: input, obfuscated }: Props) => {
    const theme = useAtomValue(themeState);
    const white = useAtomValue(whiteState);
    const black = useAtomValue(blackState);
    const ortexColor = useAtomValue(ortexColorState);

    const options = useMemo<Partial<XYChartOptions>>(() => {
        const palette = [ortexColor, "#4C8DFF", "#F0B429", "#B36AE2", "#3DBFD9", "#FF8A3D"];
        const series: XYSeries[] = [];
        (input ?? []).forEach((s, i) => {
            const color = palette[i % palette.length];
            series.push({ key: s.id, title: s.name, kind: "scatter", data: s.data ?? [], color, markerRadius: 2.5, fillOpacity: 0.8 });
            series.push({ key: `${s.id}-regression`, title: `${s.name} regression`, kind: "line", data: regressionLine(s.data ?? []), color: input.length === 1 ? (theme === "dark" ? white : black) : color, lineWidth: 1.5 });
        });
        return {
            series,
            legend: true,
            xAxis: { visible: true, gridlines: false, format: { type: "custom", formatter: (v: number) => nFormatter(v, 2) }, title: "Commodity Moves" },
            yAxis: { visible: true, gridlines: true, format: { type: "custom", formatter: (v: number) => nFormatter(v, 2) }, title: "Stock Moves" },
            snapDistance: 12,
        };
    }, [input, ortexColor, theme, white, black]);

    return (
        <div className={classNames(styles["chart-container"], obfuscated ? "obfuscated" : "")}>
            <XYPlot options={options} />
        </div>
    );
};

export default StockCommodityScatterPlotChart;
